<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-text-field
          density="compact"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          color="primary"
          v-model="search"
          hide-details
          single-line
          flat
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('search') }}</span>
        </template>
      </v-text-field>
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.root_asset_name"
          :items="advanced_search_models.root_asset_name"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('root_asset_name') }}</span>
        </template>
      </v-autocomplete>
      <v-spacer/>
      <div class="chip-container">
        <base-action-button v-if="canCreate" @action="$router.push('/importinspections')">
          <template #prepend><PhArrowSquareIn style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("import") }} {{ $store.getters.translate("inspection") }}
        </base-action-button>
        <base-action-button v-if="canCreate" @action="$router.push('/importdefects')">
          <template #prepend><PhArrowSquareIn style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("import") }} {{ $store.getters.translate("defects") }}
        </base-action-button>
        <base-action-button v-if="canCreate" @action="$router.push('/importmeasurements')">
          <template #prepend><PhArrowSquareIn style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("import") }} {{ $store.getters.translate("measurements") }}
        </base-action-button>
        <base-action-button v-if="canShowInspectionConfig" @action="$router.push('/inspectionsconfig')">
          <template #prepend><PhGear style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("config") }}
        </base-action-button>
        <base-action-button v-if="canCreate" @action="$emit('create')">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("create") }}
        </base-action-button>
      </div>
    </v-toolbar>
    <div style="overflow-x: auto; width: 100%;">
      <v-data-table
          :headers="headers"
          :items="getRecords"
          class="elevation-1"
          @click:row="handleClick"
          :search="search"
          v-bind="footer_props"
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          :show-select="false">
        <template #item.defects="{ item }">
          <v-icon v-if="item.defects === 'Ready'" color="green" size="8" class="ml-2 mr-2">mdi-circle</v-icon>
          <v-icon v-else color="red" size="8" class="ml-2 mr-2">mdi-circle</v-icon>
          <span class="sm-normal">{{ item.defects }}</span>
        </template>
        <template #item.progress="{ item }">
          <v-timeline direction="horizontal" truncate-line="both" size="x-small" style="width:120px;">
            <v-timeline-item size="x-small" :dot-color="item.progress > 0 ? '#357A83' : '#8BB3B8'"/>
            <v-timeline-item size="x-small" :dot-color="item.progress > 33 ? '#357A83' : '#8BB3B8'"/>
            <v-timeline-item size="x-small" :dot-color="item.progress > 66 ? '#357A83' : '#8BB3B8'"/>
          </v-timeline>
          <span class="sm-normal">{{ item.progress + "%" }}</span>
        </template>
        <template #item.description="{ item }">
          <v-menu :offset="[10, 0]" location="right bottom" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon size="small" variant="text" color="primary">
                <PhChatTeardropText size="24" />
              </v-btn>
            </template>
            <v-card max-width="400" min-width="300" elevation="1" color="greens-100" border="sm" class="sm-normal">
              <v-card-text class="pa-2" style="max-height: 200px; overflow-y: auto;">
                <div style="white-space: pre-wrap; word-break: break-word;">
                  {{ $lodash.truncate(item.description, { length: 600 }) }}
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </template>
        <template #item.actions="{ item }">
          <v-menu>
            <template #activator="{ props }">
              <v-btn elevation="0" icon v-bind="props">
                <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canEdit" @click="$emit('edit', item)">
                <template #prepend><PhPencilSimpleLine size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDelete" @click="deleteRecord(item.id)">
                <template #prepend><PhTrash size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import { PhArrowSquareIn, PhChatTeardropText, PhDotsThreeVertical, PhGear, PhPencilSimpleLine, PhPlus, PhTrash } from "@phosphor-icons/vue";
import BaseActionButton from "./BaseActionButton";

export default {
  components: {
    BaseActionButton,
    PhPlus, PhGear, PhArrowSquareIn, PhTrash, PhPencilSimpleLine, PhChatTeardropText, PhDotsThreeVertical,
  },
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.inspections,
      headers: helpFunctions.headers.inspections,
      all_records: [],
      records: [],
      options: {},
      footer_props: helpFunctions.footer_props,
      search: '',
      advanced_search: {
        root_asset_name: null,
      },
      advanced_search_models: {
        root_asset_name: [],
      },
    };
  },
  created() {
    if(helpFunctions.getLocalStorageValue(this.page.name + "_options")) {
      this.options = helpFunctions.getLocalStorageValue(this.page.name + "_options");
    }
    else {
      this.options = helpFunctions.table_options;
    }
  },
  methods: {
    fillAdvancedSearchArrays() {
      this.all_records.forEach(record => {
        if(record.root_asset_name && !this.advanced_search_models.root_asset_name.includes(record.root_asset_name)) {
          this.advanced_search_models.root_asset_name.push(record.root_asset_name);
        }
      });
      this.showRecords();
    },
    showRecords() {
      let sorted_array = this.all_records;
      if(this.advanced_search.root_asset_name) {
        sorted_array = sorted_array.filter(item => item.root_asset_name === this.advanced_search.root_asset_name);
      }
      this.records = sorted_array;
    },
    handleClick(event, row) {
      this.$emit('edit', row.item);
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          this.$emit('refresh');
        }
      });
    },
  },
  computed: {
    getRecords() {
      if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canCreate() {
      return this.can("create", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canShowInspectionConfig() {
      let inspections_models = Object.keys(helpFunctions.inspections_related_models);
      for (let i = 0; i < inspections_models.length; i++) {
        if(this.can("show", this.$createEntity(inspections_models[i], { id: 0 }))) {
          return true;
        }
      }
    }
  },
  watch: {
    options: {
      handler() {
        helpFunctions.setLocalStorageValue(this.page.name + "_options", this.options);
        this.showRecords();
      },
      deep: true,
    },
    advanced_search: {
      handler() {
        this.options.page = 1;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.chip-container {
  display: flex;
  gap: 8px;
  margin-right: 16px;
}
.v-timeline-item >>> .v-timeline-item__body {
  padding-block-start: 0 !important;
  padding: 0 !important;
}
.v-timeline-item >>> .v-timeline-item__opposite {
  padding-block-end: 0 !important;
  padding: 0 !important;
}
</style>